import { create } from 'zustand';
import { nanoid } from 'nanoid';
import type { AttachedFile, FileStatus } from '@/types/files';

interface FileUploadState {
  // State
  files: Map<string, AttachedFile>;

  // Actions
  addFile: (file: File) => string;
  removeFile: (id: string) => void;
  updateStatus: (id: string, status: FileStatus, error?: string) => void;
  updateProgress: (id: string, progress: number) => void;
  setFileId: (tempId: string, fileId: string, downloadUrl?: string) => void;
  clearAllFiles: () => void;
  getFile: (id: string) => AttachedFile | undefined;
}

export const useFileUploadStore = create<FileUploadState>((set, get) => ({
  files: new Map(),

  addFile: (file: File) => {
    const id = nanoid();
    const attachedFile: AttachedFile = {
      id,
      file,
      filename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      status: 'pending',
      progress: 0,
    };

    set((state) => {
      const newFiles = new Map(state.files);
      newFiles.set(id, attachedFile);
      return { files: newFiles };
    });

    return id;
  },

  removeFile: (id: string) => {
    set((state) => {
      const newFiles = new Map(state.files);
      newFiles.delete(id);
      return { files: newFiles };
    });
  },

  updateStatus: (id: string, status: FileStatus, error?: string) => {
    set((state) => {
      const newFiles = new Map(state.files);
      const file = newFiles.get(id);
      if (file) {
        newFiles.set(id, { ...file, status, error });
      }
      return { files: newFiles };
    });
  },

  updateProgress: (id: string, progress: number) => {
    set((state) => {
      const newFiles = new Map(state.files);
      const file = newFiles.get(id);
      if (file) {
        newFiles.set(id, { ...file, progress });
      }
      return { files: newFiles };
    });
  },

  setFileId: (tempId: string, fileId: string, downloadUrl?: string) => {
    set((state) => {
      const newFiles = new Map(state.files);
      const file = newFiles.get(tempId);
      if (file) {
        newFiles.set(tempId, {
          ...file,
          fileId,
          downloadUrl,
          status: 'uploaded',
          progress: 100,
        });
      }
      return { files: newFiles };
    });
  },

  clearAllFiles: () => {
    set({ files: new Map() });
  },

  getFile: (id: string) => {
    return get().files.get(id);
  },
}));
